import {
  Alert,
  Button,
  Card,
  Center,
  Group,
  List,
  Loader,
  PasswordInput,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCircleCheck } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { Logo } from '../components/Logo.js';

/**
 * Accepting an invitation.
 *
 * Public: the person has no account yet, which is the whole point. The page
 * shows only what the invitation grants and the address it was sent to —
 * enough to recognise it as genuine, nothing useful to someone who found the
 * link by accident.
 */

interface InviteView {
  email: string;
  roleName: string;
  roleKind: 'platform' | 'dealer';
  dealerNames: string[];
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expiresAt: string;
  passwordAllowed: boolean;
}

const MIN_PASSWORD = 12;

const DEAD_STATUS: Record<string, string> = {
  accepted: 'This invitation has already been used. Sign in with the address it was sent to.',
  revoked: 'This invitation was withdrawn. Ask whoever invited you to send a new one.',
  expired: 'This invitation has expired. Ask whoever invited you to send a new one.',
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Center mih="100vh" p="md">
      <Stack w={440} gap="lg">
        <Group gap="sm" justify="center">
          <div>
            <Logo height={30} />
            <Text size="sm" c="dimmed" mt={8}>
              Dealership integration console
            </Text>
          </div>
        </Group>
        {children}
      </Stack>
    </Center>
  );
}

export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<InviteView>(`/invite/${encodeURIComponent(token)}`),
    retry: false,
  });

  const form = useForm({
    initialValues: { password: '', confirm: '' },
    validate: {
      password: (v) => (v.length >= MIN_PASSWORD ? null : `At least ${MIN_PASSWORD} characters`),
      confirm: (v, values) => (v === values.password ? null : 'These do not match'),
    },
  });

  const accept = useMutation({
    mutationFn: (password: string) =>
      api.post(`/invite/${encodeURIComponent(token)}/accept`, { password }),
    onSuccess: () => navigate('/login', { replace: true }),
  });

  if (q.isLoading) {
    return (
      <Shell>
        <Center>
          <Loader />
        </Center>
      </Shell>
    );
  }

  if (q.isError) {
    return (
      <Shell>
        <Card p="lg" withBorder>
          <Stack gap="sm">
            <Text fw={600}>This invitation link is not valid</Text>
            <Text size="sm" c="dimmed">
              {(q.error as ApiError).message} Ask whoever invited you to send a new one.
            </Text>
          </Stack>
        </Card>
      </Shell>
    );
  }

  const inv = q.data!;
  const dead = DEAD_STATUS[inv.status];

  return (
    <Shell>
      <Card p="lg" withBorder>
        <Stack gap="md">
          <div>
            <Text fw={600}>You have been invited</Text>
            <Text size="sm" c="dimmed">
              This invitation was sent to <b>{inv.email}</b>.
            </Text>
          </div>

          <List
            spacing={4}
            size="sm"
            icon={
              <ThemeIcon size={18} radius="xl" variant="light" color="teal">
                <IconCircleCheck size={12} />
              </ThemeIcon>
            }
          >
            <List.Item>
              You will join as <b>{inv.roleName}</b>
            </List.Item>
            <List.Item>
              {inv.dealerNames.length > 0
                ? `For ${inv.dealerNames.join(', ')}`
                : 'Across every dealership on the platform'}
            </List.Item>
          </List>

          {dead && <Alert color="gray">{dead}</Alert>}

          {!dead && inv.passwordAllowed && (
            <form onSubmit={form.onSubmit((v) => accept.mutate(v.password))}>
              <Stack gap="sm">
                <PasswordInput
                  label="Choose a password"
                  description={`At least ${MIN_PASSWORD} characters.`}
                  required
                  {...form.getInputProps('password')}
                />
                <PasswordInput
                  label="Confirm password"
                  required
                  {...form.getInputProps('confirm')}
                />
                {accept.isError && (
                  <Alert color="red">{(accept.error as ApiError).message}</Alert>
                )}
                <Button type="submit" loading={accept.isPending} fullWidth>
                  Accept and create my account
                </Button>
              </Stack>
            </form>
          )}

          {!dead && !inv.passwordAllowed && (
            <Stack gap="sm">
              {/* With Clerk there is nothing to submit: the account is created
                  the first time they sign in, matched on the address above. */}
              <Text size="sm">
                Sign in with <b>{inv.email}</b> — however you like — and your account will be set up
                for you.
              </Text>
              <Button onClick={() => navigate('/login')} fullWidth>
                Sign in to accept
              </Button>
            </Stack>
          )}

          <Text size="xs" c="dimmed">
            {inv.status === 'pending'
              ? `This link expires on ${new Date(inv.expiresAt).toLocaleDateString()}.`
              : ''}
          </Text>
        </Stack>
      </Card>
    </Shell>
  );
}
